import { lazy, Suspense } from "react";
import { useStudioLocale } from "@/i18n";
import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";

/**
 * Bundle boundary for the Document workspace surface.
 *
 * WHY. `features/document-workspace/document-workspace-surface.tsx` (72,783
 * source bytes) plus the modules only it reaches
 * (`document-template-panel.tsx`, `connected-document-panel.tsx`) were in the
 * initial Studio chunk because two production importers loaded the component
 * statically: `IDEShellLayout` through a one-line re-export facade at
 * `surfaces/document-workspace-surface.tsx`, and `studio-dashboard-shell.tsx`
 * for the `studio.dashboard.content.documents` island. Nothing renders it at
 * first paint: `ActiveSurface` is a switch over `activeRailItem` and mounts
 * exactly one surface, `INITIAL_ACTIVE_RAIL_ITEM` is `'preview'`
 * (rail-defaults.ts), and the dashboard documents route is not the appliance
 * home. The bytes were paid by every session and read by the ones that open
 * Documents.
 *
 * Same shape, same reasoning, as `ReportWorkspaceSurfaceLazy` in
 * `report-workspace-surface-lazy.tsx` (#2638) and `EvidenceSurfaceLazy`.
 *
 * The lazy type is created ONCE at module scope. Recreating it inside a
 * component makes React treat every render as a new lazy component and repaint
 * the Suspense fallback — the trap `App.tsx` documents for its route elements.
 *
 * The dynamic import names the feature module directly rather than the
 * `surfaces/document-workspace-surface.tsx` facade: importing a re-export
 * facade makes rolldown emit a second, ~130-byte chunk whose NAME still
 * matches the surface, which is the false-green the
 * `evidence-surface-copy-eager` note records having hit when that gate was
 * first written against a name prefix.
 *
 * The Suspense boundary lives here rather than at the two call sites so that
 * both of them get the same wait state, and so a third call site cannot
 * reintroduce a static import by accident. The `/__document-workspace` smoke
 * harness stays on its own `React.lazy` route in `App.tsx` and is not a
 * first-paint importer.
 */
const DocumentWorkspaceSurfaceChunk = lazy(() =>
  import("@/features/document-workspace/document-workspace-surface").then(
    (m) => ({
      default: m.DocumentWorkspaceSurface,
    }),
  ),
);

/**
 * The wait state for the Document chunk.
 *
 * Deliberately the generic "loading" register rather than any of the surface's
 * own framing, and deliberately NOT read from `document-workspace-copy.ts`.
 */
const LOADING_COPY: Record<LocaleCode, string> = {
  en: "Loading document workspace…",
  ko: "문서 작업 공간을 불러오는 중…",
  ja: "ドキュメントワークスペースを読み込んでいます…",
  zh: "正在加载文档工作区…",
  "zh-TW": "正在載入文件工作區…",
};

/** Exported for the test that pins the wait state in every locale. */
export function DocumentWorkspaceSurfaceLoadingPlaceholder() {
  const { locale } = useStudioLocale();
  return (
    <div
      data-testid="document-workspace-surface-loading-placeholder"
      className="flex h-full items-center justify-center text-sm text-muted-foreground"
    >
      {LOADING_COPY[locale] ?? LOADING_COPY.en}
    </div>
  );
}

/**
 * Drop-in replacement for a static `<DocumentWorkspaceSurface />`.
 *
 * The Suspense boundary lives here rather than at the call sites so that a
 * second call site cannot reintroduce a static import by accident.
 */
export function DocumentWorkspaceSurfaceLazy() {
  return (
    <Suspense fallback={<DocumentWorkspaceSurfaceLoadingPlaceholder />}>
      <DocumentWorkspaceSurfaceChunk />
    </Suspense>
  );
}
